import {
  ClockCircleOutlined,
  LockFilled,
  RightOutlined,
  TeamOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { Button, Space, Tag, Tooltip } from "antd";
import React from "react";
import { ReadMore } from "~/components/readmore/readMore.jsx";
import { clubHasFlight } from "~/featureMatrix.jsx";
import { $t } from "~/i18n.js";
import { parseMinutes, toLocaleDate } from "~/lib/localize.js";

const Value = ({ value }) => (
  <Space>
    <p>{value}</p>
  </Space>
);

const day = (mobile) => ({
  title: $t("Datum"),
  dataIndex: "datum",
  width: 120,
  render: (_, dag) => (
    <p>
      {toLocaleDate(new Date(dag.datum), {
        weekday: "short",
        month: "short",
        day: "numeric",
      })}
      {mobile &&
        dag.briefing_tijd &&
        dag.briefing_tijd !== "09:00" &&
        ` om ${dag.briefing_tijd}`}
    </p>
  ),
});

const vliegveld = () => ({
  title: $t("Locatie"),
  dataIndex: "vertrek_vliegveld",
});
const soort = () => ({
  title: $t("Dag"),
  dataIndex: "type_dag",
  render: (value, dag) => {
    return (
      <p>
        {!dag.gaat_door && (
          <Tag style={{ marginRight: 4 }} color="red">
            {$t("Geannuleerd")}
          </Tag>
        )}

        <span style={{ margin: "0 4px" }}>
          {value || $t("Automatisch aangemaakt")}
          {dag.titel && dag.titel.length > 0 && ": " + dag.titel}
        </span>

        {dag.gaat_door && dag.status === "locked" && (
          <Tooltip placement="top" title={$t("Dag is gesloten voor wijzigingen")}>
            <LockFilled style={{ color: "black", marginRight: 4 }} />
          </Tooltip>
        )}

        {dag.gaat_door && dag.status === "closed" && (
          <Tooltip
            placement="top"
            title={$t("Dag is gesloten maar open voor wijzigingen")}
          >
            <UnlockOutlined style={{ color: "black", marginRight: 4 }} />
          </Tooltip>
        )}

        {dag.gaat_door && dag.status === "open" && dag.can_start && dag.is_today && (
          dag.hasSiblings && !dag.accept_flights
            ? <Tag style={{ marginRight: 4 }} color="default">{$t("Niet actief")}</Tag>
            : <Tag style={{ marginRight: 4 }} color="blue">{$t("Open")}</Tag>
        )}

        {!dag.is_vliegend && <TeamOutlined />}
      </p>
    );
  },
});

const starts = (totals) => ({
  title: $t("Starts"),
  dataIndex: "starts",
  width: 60,
  render: (_r, row) => (
    <Value value={totals[row.dag_id] && totals[row.dag_id].starts} />
  ),
});

const uren = (totals) => ({
  title: $t("Uren"),
  dataIndex: "uren",
  width: 60,
  render: (_r, row) => (
    <Value
      value={parseMinutes(totals[row.dag_id] && totals[row.dag_id].minutes)}
    />
  ),
});
const sleep = (totals) => ({
  title: $t("Sleep"),
  dataIndex: "sleep",
  width: 60,
  render: (_r, row) => (
    <Value value={totals[row.dag_id] && totals[row.dag_id].sleep} />
  ),
});

const aanmeldingen = (
  totals,
  mijn_aanmeldingen,
  mijn_diensten,
  mijn_registration_lists
) => ({
  title: $t("Aanmeldingen"),
  dataIndex: "aanmeldingen",
  render: (_r, row) =>
    row.gaat_door && (
      <div>
        <span style={{ marginRight: 6 }}>
          {totals[row.dag_id] && totals[row.dag_id].aanmeldingen}
        </span>

        {mijn_diensten &&
        mijn_diensten[row.dag_id] &&
        Array.isArray(mijn_diensten[row.dag_id])
          ? mijn_diensten[row.dag_id].map((dienst, key) => (
              <Tag key={key} color="geekblue">
                {dienst.name}
              </Tag>
            ))
          : mijn_aanmeldingen &&
            mijn_aanmeldingen[row.dag_id] && (
              <Tag color="geekblue">{$t("ingeschreven")}</Tag>
            )}

        {mijn_registration_lists &&
          mijn_registration_lists[row.dag_id] &&
          Array.isArray(mijn_registration_lists[row.dag_id]) &&
          mijn_registration_lists[row.dag_id].map((list, key) => (
            <Tag key={key} color={list.is_waitlist ? "default" : "green"} icon={list.is_waitlist && <ClockCircleOutlined />}>
              {list.registration_list_name}
            </Tag>
          ))}
      </div>
    ),
});

const notitie = (totals) => ({
  title: $t("Notitie"),
  dataIndex: "notitie",
  key: "notitie",
  render: (_r, row) => (
    <Value
      value={
        totals[row.dag_id] &&
        totals[row.dag_id].notitie && (
          <ReadMore
            text={totals[row.dag_id].notitie.bericht}
            hideMore
            maxLength={60}
          />
        )
      }
    />
  ),
});

const actions = {
  title: "",
  width: 40,
  render: () => (
    <Space>
      <Button
        type="primary"
        icon={<RightOutlined />}
        size="small"
        shape="circle"
      />
    </Space>
  ),
};

export const dagenColumns = (
  totals,
  mijn_aanmeldingen,
  mijn_diensten,
  mijn_registration_lists,
  link_to
) => {
  const briefing_tijd = { title: $t("Briefing"), dataIndex: "briefing_tijd" };

  if (!link_to || link_to === "register") {
    return [
      day(),
      vliegveld(),
      briefing_tijd,
      soort(),
      aanmeldingen(totals, mijn_aanmeldingen, mijn_diensten, mijn_registration_lists),
      actions,
    ];
  }
  if (link_to === "dailyreport") {
    return [
      day(),
      vliegveld(),
      briefing_tijd,
      soort(),
      clubHasFlight() ? starts(totals) : { key: "null" },
      notitie(totals),
      actions,
    ];
  }
  if (link_to === "start") {
    return [
      day(),
      vliegveld(),
      briefing_tijd,
      soort(),
      starts(totals),
      uren(totals),
      actions,
    ];
  }
  if (link_to === "tow") {
    return [
      day(),
      vliegveld(),
      soort(),
      starts(totals),
      sleep(totals),
      actions,
    ];
  }
};

export const dagenMobileColumns = (
  totals,
  mijn_aanmeldingen,
  mijn_diensten,
  mijn_registration_lists,
  link_to
) => {
  if (!link_to || link_to === "register") {
    return [
      day(true),
      soort(),
      aanmeldingen(totals, mijn_aanmeldingen, mijn_diensten, mijn_registration_lists),
    ];
  }
  if (link_to === "dailyreport") {
    return [day(true), vliegveld(), soort(), starts(totals)];
  }
  if (link_to === "start") {
    return [day(true), vliegveld(), soort(), starts(totals), sleep(totals)];
  }
  if (link_to === "tow") {
    return [day(true), vliegveld(), soort(), starts(totals), sleep(totals)];
  }
};
